import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Loader2, X } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { uploadFile } from '@/services/storage';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  storagePath: string;
}

const ImageUpload = ({ value, onChange, storagePath }: ImageUploadProps) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return;

    setIsUploading(true);
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
      });

      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${storagePath}/${Date.now()}.${ext}`;
      const downloadUrl = await uploadFile(path, compressed);
      onChange(downloadUrl);
    } catch (err) {
      console.error('Failed to upload image:', err);
    } finally {
      setIsUploading(false);
    }
  }, [storagePath, onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    onChange('');
  };

  if (value) {
    return (
      <div className="relative rounded-2xl overflow-hidden border border-gray-200 dark:border-dark-600">
        <img
          src={value}
          alt=""
          className="w-full h-48 object-cover"
        />
        <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors group flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-white rounded-xl text-sm font-medium text-dark-800"
          >
            {t('widgets.changeImage')}
          </button>
          <button
            type="button"
            onClick={handleRemove}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-white rounded-xl text-red-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        disabled={isUploading}
        className={`
          w-full flex flex-col items-center justify-center gap-2 p-8 rounded-2xl border-2 border-dashed transition-colors cursor-pointer
          ${isDragging
            ? 'border-lime-400 bg-lime-50 dark:bg-lime-900/20'
            : 'border-gray-300 dark:border-dark-600 hover:border-lime-400 hover:bg-gray-50 dark:hover:bg-dark-700'
          }
          ${isUploading ? 'pointer-events-none opacity-60' : ''}
        `}
      >
        {isUploading ? (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('widgets.uploadingImage')}</p>
          </>
        ) : (
          <>
            <Upload className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            <p className="text-sm font-medium text-dark-800 dark:text-gray-200">{t('widgets.dragOrClick')}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">{t('widgets.maxFileSize')}</p>
          </>
        )}
      </button>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleInputChange}
      />
    </div>
  );
};

export default ImageUpload;
